import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, Wand2, Upload, Link, RefreshCw, X, Check, Image, Download,
  Loader2,
} from 'lucide-react';
import { apiClient, generateAdImage } from '../../services/api';
import GlassButton from '../ui/GlassButton';
import './ImagePickerModal.css';

// ── Types ─────────────────────────────────────────────────────────────────

export interface ImagePickerMeta {
  source: 'pexels' | 'ai' | 'upload' | 'url';
  /** AI tab only — the prompt that produced the image */
  prompt?: string;
  /** Pexels tab only */
  photographer?: string;
  /** Pexels tab only — for future exclusion tracking */
  pexels_id?: number;
}

export interface ImagePickerModalProps {
  /** Controls visibility — parent owns open/close state. */
  open: boolean;
  /** Called when the user clicks Cancel, presses Escape, or clicks outside. */
  onClose: () => void;
  /** Title shown in the modal header. Default: "Choose an Image". */
  title?: string;

  /** Seed for the Pexels query input. Defaults to "". */
  initialPexelsQuery?: string;
  /** Seed for the AI prompt textarea. Defaults to "". */
  initialAiPrompt?: string;
  /**
   * Platform passed to /multichannel/ad-copy/generate-image.
   * Defaults to "instagram".
   */
  aiPlatform?: 'instagram' | 'facebook' | 'linkedin' | 'twitter' | 'google_search' | 'meta_facebook' | 'linkedin_sponsored';
  /** Brand guide id passed to /multichannel/ad-copy/generate-image. Optional. */
  brandGuideId?: string;

  /**
   * Pexels search strategy. Required when showPexelsTab !== false.
   * The modal stays agnostic — each caller provides its own fetcher.
   */
  pexelsFetcher?: (
    query: string,
    excludedIds: number[],
  ) => Promise<{ image_url: string; pexels_id?: number; photographer?: string }>;

  /** Optional flags to hide tabs on a per-caller basis. All default true. */
  showPexelsTab?: boolean;
  showAiTab?: boolean;
  showUploadTab?: boolean;
  showUrlTab?: boolean;

  /**
   * Persistence callback — invoked when the user clicks Apply with a chosen URL.
   * Must throw / reject to keep the modal open with an error; resolve / return void
   * to close.
   */
  onImageSelected: (url: string, meta: ImagePickerMeta) => Promise<void> | void;
}

// ── Helpers ───────────────────────────────────────────────────────────────

type TabKey = 'pexels' | 'ai' | 'upload' | 'url';

interface TabDef {
  key: TabKey;
  label: string;
  Icon: React.FC<{ size?: number }>;
}

const ALL_TABS: TabDef[] = [
  { key: 'pexels', label: 'Pexels',  Icon: Search  },
  { key: 'ai',     label: 'AI',      Icon: Wand2   },
  { key: 'upload', label: 'Upload',  Icon: Upload  },
  { key: 'url',    label: 'URL',     Icon: Link    },
];

// ── Component ─────────────────────────────────────────────────────────────

const ImagePickerModal: React.FC<ImagePickerModalProps> = ({
  open,
  onClose,
  title = 'Choose an Image',
  initialPexelsQuery = '',
  initialAiPrompt = '',
  aiPlatform = 'instagram',
  brandGuideId,
  pexelsFetcher,
  showPexelsTab = true,
  showAiTab = true,
  showUploadTab = true,
  showUrlTab = true,
  onImageSelected,
}) => {
  // Determine which tabs are visible
  const visibleTabs = ALL_TABS.filter(t => {
    if (t.key === 'pexels') return showPexelsTab && !!pexelsFetcher;
    if (t.key === 'ai')     return showAiTab;
    if (t.key === 'upload') return showUploadTab;
    if (t.key === 'url')    return showUrlTab;
    return true;
  });

  const firstTab = visibleTabs[0]?.key ?? 'url';

  // ── State ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]           = useState<TabKey>(firstTab);
  const [previewUrl, setPreviewUrl]         = useState<string | null>(null);
  const [previewMeta, setPreviewMeta]       = useState<ImagePickerMeta | null>(null);

  const [pexelsQuery, setPexelsQuery]       = useState(initialPexelsQuery);
  const [aiPrompt, setAiPrompt]             = useState(initialAiPrompt);
  const [urlInput, setUrlInput]             = useState('');

  /** Accumulates pexels IDs already shown so "Try Another" avoids repeats. */
  const [excludedPexelsIds, setExcludedPexelsIds] = useState<number[]>([]);

  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [applying, setApplying]             = useState(false);

  // Ref used for mousedown-outside detection
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef   = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Reset when modal opens ─────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setActiveTab(firstTab);
      setPreviewUrl(null);
      setPreviewMeta(null);
      setPexelsQuery(initialPexelsQuery);
      setAiPrompt(initialAiPrompt);
      setUrlInput('');
      setExcludedPexelsIds([]);
      setLoading(false);
      setError(null);
      setApplying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Keyboard: Escape → close ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // ── Click outside → close ─────────────────────────────────────────────
  const handleOverlayMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  // ── Pexels fetch ───────────────────────────────────────────────────────
  const handlePexelsFetch = useCallback(async (isRetry = false) => {
    if (!pexelsFetcher) return;
    setLoading(true);
    setError(null);
    const idsToExclude = isRetry
      ? (previewMeta?.pexels_id ? [...excludedPexelsIds, previewMeta.pexels_id] : excludedPexelsIds)
      : excludedPexelsIds;
    if (isRetry && previewMeta?.pexels_id) {
      setExcludedPexelsIds(idsToExclude);
    }
    try {
      const result = await pexelsFetcher(pexelsQuery, idsToExclude);
      setPreviewUrl(result.image_url);
      setPreviewMeta({
        source: 'pexels',
        photographer: result.photographer,
        pexels_id: result.pexels_id,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to fetch image from Pexels.');
    } finally {
      setLoading(false);
    }
  }, [pexelsFetcher, pexelsQuery, excludedPexelsIds, previewMeta]);

  // ── AI generate ────────────────────────────────────────────────────────
  const handleAiGenerate = useCallback(async () => {
    if (!aiPrompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await generateAdImage({
        visual_concept: aiPrompt.trim(),
        platform: aiPlatform,
        brand_guide_id: brandGuideId,
      });
      setPreviewUrl(result.image_url);
      setPreviewMeta({
        source: 'ai',
        prompt: result.image_prompt,
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to generate image.');
    } finally {
      setLoading(false);
    }
  }, [aiPrompt, aiPlatform, brandGuideId]);

  // ── File upload ────────────────────────────────────────────────────────
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await apiClient.post('/brand-guides/upload-asset', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = uploadRes.data.url || uploadRes.data.path;
      setPreviewUrl(url);
      setPreviewMeta({ source: 'upload' });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to upload file.');
    } finally {
      setLoading(false);
      // Reset the file input so the same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  // ── URL tab: commit on blur or Enter ───────────────────────────────────
  const handleUrlCommit = useCallback(() => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    setPreviewUrl(trimmed);
    setPreviewMeta({ source: 'url' });
    setError(null);
  }, [urlInput]);

  // ── Apply ──────────────────────────────────────────────────────────────
  const handleApply = useCallback(async () => {
    if (!previewUrl || !previewMeta) return;
    setApplying(true);
    setError(null);
    try {
      await onImageSelected(previewUrl, previewMeta);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save image. Please try again.');
    } finally {
      setApplying(false);
    }
  }, [previewUrl, previewMeta, onImageSelected, onClose]);

  if (!open) return null;

  // ── Render tab content ─────────────────────────────────────────────────
  const renderTabPane = () => {
    switch (activeTab) {
      case 'pexels':
        return (
          <div className="image-picker-tab-pane">
            <div>
              <div className="image-picker-field-label">Search query</div>
              <input
                className="image-picker-input"
                type="text"
                readOnly
                value={pexelsQuery}
                placeholder="Pexels search uses this task's image concept"
              />
              <div className="image-picker-field-hint">
                Pexels search uses this task's image concept — the query above is informational.
              </div>
            </div>

            {/* Preview */}
            <div className="image-picker-preview-frame">
              {previewUrl && previewMeta?.source === 'pexels' ? (
                <img src={previewUrl} alt="Pexels preview" />
              ) : (
                <div className="image-picker-preview-empty">
                  <Image size={28} />
                  <span>Click "Fetch Image" to load a photo</span>
                </div>
              )}
            </div>

            {previewMeta?.photographer && (
              <div className="image-picker-field-label" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                Photo by {previewMeta.photographer} on Pexels
              </div>
            )}

            <GlassButton
              variant="secondary"
              onClick={() => handlePexelsFetch(false)}
              disabled={loading || applying}
            >
              {loading ? <Loader2 size={14} className="ipm-spin" /> : <Search size={14} />}
              {loading ? 'Fetching…' : 'Fetch Image'}
            </GlassButton>

            {previewUrl && previewMeta?.source === 'pexels' && (
              <button
                className="image-picker-try-another"
                onClick={() => handlePexelsFetch(true)}
                disabled={loading || applying}
              >
                {loading ? <Loader2 size={14} className="ipm-spin" /> : <RefreshCw size={14} />}
                Try Another
              </button>
            )}
          </div>
        );

      case 'ai':
        return (
          <div className="image-picker-tab-pane">
            <div>
              <div className="image-picker-field-label">Visual concept / prompt</div>
              <textarea
                className="image-picker-textarea"
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                placeholder="Describe the image you want to generate…"
                rows={3}
              />
            </div>

            {/* Preview */}
            <div className="image-picker-preview-frame">
              {previewUrl && previewMeta?.source === 'ai' ? (
                <img src={previewUrl} alt="AI generated preview" />
              ) : (
                <div className="image-picker-preview-empty">
                  <Wand2 size={28} />
                  <span>Enter a prompt and click "Generate"</span>
                </div>
              )}
            </div>

            <GlassButton
              variant="secondary"
              onClick={handleAiGenerate}
              disabled={!aiPrompt.trim() || loading || applying}
            >
              {loading ? <Loader2 size={14} className="ipm-spin" /> : <Wand2 size={14} />}
              {loading ? 'Generating…' : 'Generate'}
            </GlassButton>
          </div>
        );

      case 'upload':
        return (
          <div className="image-picker-tab-pane">
            {/* Preview */}
            <div className="image-picker-preview-frame">
              {previewUrl && previewMeta?.source === 'upload' ? (
                <img src={previewUrl} alt="Uploaded preview" />
              ) : (
                <div className="image-picker-preview-empty">
                  <Upload size={28} />
                  <span>No file uploaded yet</span>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <GlassButton
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || applying}
            >
              {loading ? <Loader2 size={14} className="ipm-spin" /> : <Upload size={14} />}
              {loading ? 'Uploading…' : 'Choose File'}
            </GlassButton>
          </div>
        );

      case 'url':
        return (
          <div className="image-picker-tab-pane">
            <div>
              <div className="image-picker-field-label">Image URL</div>
              <input
                className="image-picker-input"
                type="url"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onBlur={handleUrlCommit}
                onKeyDown={e => { if (e.key === 'Enter') handleUrlCommit(); }}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            {/* Preview */}
            <div className="image-picker-preview-frame">
              {previewUrl && previewMeta?.source === 'url' ? (
                <img src={previewUrl} alt="URL preview" />
              ) : (
                <div className="image-picker-preview-empty">
                  <Link size={28} />
                  <span>Paste a URL above and press Enter</span>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="image-picker-overlay"
      ref={overlayRef}
      onMouseDown={handleOverlayMouseDown}
    >
      <div className="image-picker-modal" ref={modalRef}>
        {/* Header */}
        <div className="image-picker-header">
          <h3>{title}</h3>
          <button className="image-picker-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="image-picker-body">
          {/* Tab strip */}
          {visibleTabs.length > 1 && (
            <div className="image-picker-tabs">
              {visibleTabs.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  className={`image-picker-tab${activeTab === key ? ' active' : ''}`}
                  onClick={() => { setActiveTab(key); setError(null); }}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Tab content */}
          {renderTabPane()}

          {/* Error */}
          {error && <div className="image-picker-error">{error}</div>}
        </div>

        {/* Footer */}
        <div className="image-picker-footer">
          <GlassButton variant="secondary" onClick={onClose} disabled={applying}>
            Cancel
          </GlassButton>
          <GlassButton
            variant="primary"
            onClick={handleApply}
            disabled={!previewUrl || !previewMeta || applying || loading}
          >
            {applying ? <Loader2 size={14} className="ipm-spin" /> : <Check size={14} />}
            {applying ? 'Saving…' : 'Apply'}
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

export default ImagePickerModal;
